/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, breakpoints } from '@coursera/cds-core';
import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import CompactCollectionHeadline from 'bundles/unified-common/components/compact-collection/CompactCollectionHeadline';
import CompactCollectionProductCard from 'bundles/unified-common/components/compact-collection/CompactCollectionProductCard';

type Props = {
  headline: string;
  headlineTarget?: string;
  collectionId: string;
  collectionData: CarouselCollection;
  carouselSectionIndex?: number;
  pageConfigComponentTrackingData?: PageConfigComponentTrackingData;
};

const styles = {
  container: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: calc(var(--cds-spacing-150) / 2 + var(--cds-spacing-100));
    background-color: var(--cds-color-emphasis-primary-background-weak);
    padding: calc(var(--cds-spacing-150) / 2 + var(--cds-spacing-100));
    border-radius: var(--cds-border-radius-100);

    ${breakpoints.down('sm')} {
      padding: calc(var(--cds-spacing-100) + var(--cds-spacing-50));
      gap: var(--cds-spacing-100);
    }
  `,
  flexGrow: css`
    flex-grow: 1;
  `,
};

const CompactCollection = ({
  headline,
  headlineTarget,
  collectionId,
  collectionData,
  carouselSectionIndex,
  pageConfigComponentTrackingData,
}: Props) => {
  const track = useTracker();

  const handleHeadlineClick = () => {
    track('click_button', {
      button: {
        name: 'view_all_products',
        linkURL: headlineTarget,
        linkName: headline,
        linkType: 'internal',
        ...(pageConfigComponentTrackingData && { pageConfigComponent: pageConfigComponentTrackingData }),
      },
    });
  };

  return (
    <div css={styles.container}>
      <CompactCollectionHeadline headline={headline} headlineTarget={headlineTarget} onClick={handleHeadlineClick} />
      <Grid container direction="column" spacing={8} css={styles.flexGrow}>
        {collectionData.entities.map((entity, index) => (
          <Grid item key={entity.id} css={collectionData.entities.length > 2 ? styles.flexGrow : undefined}>
            <CompactCollectionProductCard
              entity={entity}
              entityIndex={index}
              additionalV3TrackingData={{
                carousel: {
                  id: collectionId,
                  model: collectionData.collectionId ?? '',
                  name: headline,
                  section: carouselSectionIndex,
                },
                simpleCollectionRecommender: collectionData.recommender,
                pageConfigComponent: pageConfigComponentTrackingData,
              }}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default CompactCollection;
